import React, { useState, useEffect, useCallback } from 'react';
import TabContainerHOC from './hocs/TabContainerHOC';
import Notification from '../global/Notification';
import StickySaveBar from '../global/StickySaveBar';
import { PageProps } from '../../types';
import TaglineColorPickers from './components/TaglineColorPickers';
import TaglineLivePreview from './components/TaglineLivePreview';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import FontSizeSlider from './components/FontSizeSlider';
import { getCustomizationSection, saveCustomizationSection } from '../../lib/customization/customizationService';

const DEFAULT_FONT_SIZE = 11;
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 22;
export default function TaglinePage({ model }: PageProps) {
    const [tagline, setTagline] = useState('');
    const [taglineColor, setTaglineColor] = useState('#000000');
    const [url, setUrl] = useState('');
    const [span, setSpan] = useState('');
    const [spanColor, setSpanColor] = useState('#000000');
    const [taglineFontSize, setTaglineFontSize] = useState(DEFAULT_FONT_SIZE);
    const [spanFontSize, setSpanFontSize] = useState(DEFAULT_FONT_SIZE);

    const [taglineBgColor, setTaglineBgColor] = useState('#BA0C2F');
    const [highlightedElement, setHighlightedElement] = useState<string | null>(null);

    const [originalData, setOriginalData] = useState<{ tagline: string; taglineColor: string; url: string; span: string; spanColor: string; taglineBgColor: string; taglineFontSize: number; spanFontSize: number } | null>(null);
    const [calling, setCalling] = useState(false);
    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success',
    });
    const [saveError, setSaveError] = useState<string | null>(null);
    const [showTagline, setShowTagline] = useState(true);

    const closeNotification = useCallback(() => setNotification({ message: null, type: 'success' }), []);
    const dismissSaveError = useCallback(() => setSaveError(null), []);

    const hasChanges = !!originalData && (
        tagline !== originalData.tagline ||
        taglineColor !== originalData.taglineColor ||
        url !== originalData.url ||
        span !== originalData.span ||
        spanColor !== originalData.spanColor ||
        taglineBgColor !== originalData.taglineBgColor ||
        taglineFontSize !== originalData.taglineFontSize ||
        spanFontSize !== originalData.spanFontSize
    );

    const fetchTagline = async () => {
        setCalling(true);
        try {
            const data = await getCustomizationSection<any>(model.storeId, 'Tagline');

            const { p, pColor, url: apiUrl, span, spanColor, bgColor, pFontSize: apiPFontSize, spanFontSize: apiSpanFontSize } = data;

            const baseline = {
                tagline: p || '',
                taglineColor: pColor || '#000000',
                url: apiUrl || '',
                span: span || '',
                spanColor: spanColor || '#000000',
                taglineBgColor: bgColor || '#BA0C2F',
                taglineFontSize: apiPFontSize ? Number(apiPFontSize) : DEFAULT_FONT_SIZE,
                spanFontSize: apiSpanFontSize ? Number(apiSpanFontSize) : DEFAULT_FONT_SIZE,
            };

            setTagline(baseline.tagline);
            setTaglineColor(baseline.taglineColor);
            setUrl(baseline.url);
            setSpan(baseline.span);
            setSpanColor(baseline.spanColor);
            setTaglineBgColor(baseline.taglineBgColor);
            setTaglineFontSize(baseline.taglineFontSize);
            setSpanFontSize(baseline.spanFontSize);
            setOriginalData(baseline);
        } catch (error) {
            console.error('Error fetching tagline:', error);
            setNotification({ message: 'Failed to load tagline. Please try again later.', type: 'error' });
        } finally {
            setCalling(false);
        }
    };

    useEffect(() => {
        fetchTagline();
    }, []);

    const saveTagline = async (): Promise<boolean> => {
        const payload = {
            p: tagline || '',
            pColor: taglineColor || '#000000',
            url: url || '',
            span: span || '',
            spanColor: spanColor || '#000000',
            bgColor: taglineBgColor,
            pFontSize: taglineFontSize,
            spanFontSize: spanFontSize,
        };

        setCalling(true);
        setSaveError(null);
        try {
            await saveCustomizationSection({
                storeId: model.storeId,
                metafield: 'Tagline',
                section: 'tagline',
                payload: payload,
                lastValue: originalData,
                newValue: { tagline, taglineColor, url, span, spanColor, taglineBgColor, taglineFontSize, spanFontSize },
            });

            setNotification({ message: 'Tagline saved successfully!', type: 'success' });
            setOriginalData({ tagline, taglineColor, url, span, spanColor, taglineBgColor, taglineFontSize, spanFontSize });
            return true;
        } catch (error) {
            console.error('Error saving tagline:', error);
            setSaveError('Failed to save tagline. Please try again.');
            return false;
        } finally {
            setCalling(false);
        }
    };

    const unsavedChangesModal = useUnsavedChangesGuard({ hasChanges, save: saveTagline });

    const handleCancel = () => {
        if (originalData) {
            setTagline(originalData.tagline);
            setTaglineColor(originalData.taglineColor);
            setUrl(originalData.url);
            setSpan(originalData.span);
            setSpanColor(originalData.spanColor);
            setTaglineBgColor(originalData.taglineBgColor);
            setTaglineFontSize(originalData.taglineFontSize);
            setSpanFontSize(originalData.spanFontSize);
        }
        setSaveError(null);
    };

    return (
        <>
            <TabContainerHOC title="Tagline" description={<Description />} containerClass="tagline" loading={calling}>
                {showTagline && (
                    <>
                        <div className="row title-inputs">
                            <div className="col-sm col-left">
                                <h2>Edit the Site Tagline</h2>
                            </div>
                        </div>

                        <hr className="" />

                        <div className="tagline-container">
                            <div className="tagline-input-wrapper">
                                <p>
                                    <b>Text 1</b>
                                    <span className="esl"> (This text will appear to the left of Text 2)</span>
                                </p>
                                <div className="color-input-wrapper-tagline">
                                    <input
                                        type="text"
                                        value={span}
                                        onChange={(e) => setSpan(e.target.value)}
                                        className={`input-round form-control ${highlightedElement === 'text1' ? 'highlighted-input' : ''}`}
                                        placeholder="Type your tagline here."
                                        disabled={calling}
                                        onFocus={() => setHighlightedElement('text1')}
                                        onBlur={() => setHighlightedElement(null)}
                                    />
                                </div>
                            </div>

                            <div className="tagline-input-wrapper">
                                <p>
                                    <b>Text 2</b>
                                    <span className="esl"> (If a URL is provided, this text will be a clickable link)</span>
                                </p>
                                <div className="color-input-wrapper-tagline">
                                    <input
                                        type="text"
                                        value={tagline}
                                        onChange={(e) => setTagline(e.target.value)}
                                        className={`input-round form-control ${highlightedElement === 'text2' ? 'highlighted-input' : ''}`}
                                        placeholder="Enter the span value here."
                                        disabled={calling}
                                        onFocus={() => setHighlightedElement('text2')}
                                        onBlur={() => setHighlightedElement(null)}
                                    />
                                </div>
                            </div>

                            <div className="tagline-input-wrapper url">
                                <div className="tagline-input-field">
                                    <p>
                                        <b>URL</b>
                                        <span className="esl"> (Must be a valid URL)</span>
                                    </p>
                                    <input
                                        type="text"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        className="input-round form-control mt-2"
                                        placeholder="Enter the associated URL here."
                                        disabled={calling}
                                    />
                                </div>
                            </div>

                            <div className="tagline-input-wrapper font-sizes">
                                <FontSizeSlider
                                    label="Text 1 font size"
                                    value={spanFontSize}
                                    onChange={setSpanFontSize}
                                    min={MIN_FONT_SIZE}
                                    max={MAX_FONT_SIZE}
                                    disabled={calling}
                                    onFocus={() => setHighlightedElement('text1')}
                                    onBlur={() => setHighlightedElement(null)}
                                />
                                <FontSizeSlider
                                    label="Text 2 font size"
                                    value={taglineFontSize}
                                    onChange={setTaglineFontSize}
                                    min={MIN_FONT_SIZE}
                                    max={MAX_FONT_SIZE}
                                    disabled={calling}
                                    onFocus={() => setHighlightedElement('text2')}
                                    onBlur={() => setHighlightedElement(null)}
                                />
                            </div>
                        </div>

                        <div className="tagline-container tagline-colors-section">
                            <TaglineColorPickers
                                bg={taglineBgColor}
                                setBg={setTaglineBgColor}
                                text1={spanColor}
                                setText1={setSpanColor}
                                text2={taglineColor}
                                setText2={setTaglineColor}
                                setHighlightedElement={setHighlightedElement}
                                disabled={calling}
                            />

                            <TaglineLivePreview
                                tagline={tagline}
                                span={span}
                                url={url}
                                bgColor={taglineBgColor}
                                taglineColor={taglineColor}
                                spanColor={spanColor}
                                taglineFontSize={taglineFontSize}
                                spanFontSize={spanFontSize}
                                highlightedElement={highlightedElement}
                            />
                        </div>

                    </>
                )}
                {unsavedChangesModal}
            </TabContainerHOC>
            <StickySaveBar
                visible={hasChanges}
                saving={calling}
                onSave={saveTagline}
                onCancel={handleCancel}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
        </>
    );
}

const Description = () => (
    <ul>
        <li>
            Provide a short text labeled as <b>Text 1</b>, which appears on the left.
        </li>
        <li>
            Add a second text labeled as <b>Text 2</b>, with a recommended maximum of 300 characters.
        </li>
        <li>
            Optionally, associate a valid <b>URL</b> with Text 2, which will be linked accordingly.
        </li>
        <li>
            Adjust the <b>font size</b> of each text independently, from {MIN_FONT_SIZE}px to {MAX_FONT_SIZE}px.
        </li>
    </ul>
);